import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
} from './types'
import { fromAssemblyMateDocumentJSON } from '../../assembly/document'
import type { Matrix4Tuple } from '../../imported-model/loaders/types'
import { PUPPY_PACKAGE_VERSION } from './types'
import {
  readPuppyDatumExtensionFromAssemblyExtensions,
  writePuppyDatumExtensionToAssemblyExtensions,
} from './puppyDatumExtension'
//------ 线性零部件阵列离线包定义持久化 开始------
import {
  readPuppyLinearComponentPatternExtensionFromAssemblyExtensions,
  writePuppyLinearComponentPatternExtensionToAssemblyExtensions,
} from './puppyLinearComponentPatternExtension'
//------ 线性零部件阵列离线包定义持久化 结束------

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/**
 * 读取必填非空字符串。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 字段值。
 */
function readRequiredString(record: Record<string, unknown>, key: string): string {
  const value = record[key]
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`PuppyCAD assembly.json 字段缺失或无效: ${key}`)
  }
  return value.trim()
}

/**
 * 读取可选非空字符串。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 字段值。
 */
function readOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 读取可选记录对象。
 *
 * @param value 未知值。
 * @returns 记录对象副本。
 */
function readOptionalRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? { ...value } : undefined
}

/**
 * 解析 assembly.json 顶层扩展字段。
 *
 * @param value 未知值。
 * @returns 扩展字段副本；不存在时返回 `undefined`。
 */
function parseAssemblyExtensions(value: unknown): Record<string, unknown> | undefined {
  const extensions = readOptionalRecord(value)
  if (!extensions) {
    return undefined
  }

  /**
   * datums 是当前协议中会影响 CAD 主状态恢复的扩展，必须在解析阶段校验；
   * 其它未知扩展保持透传，交给对应能力的解析器处理。
   */
  const datumExtension = readPuppyDatumExtensionFromAssemblyExtensions(extensions)
  const extensionsWithDatums = writePuppyDatumExtensionToAssemblyExtensions(extensions, datumExtension)
  //------ 线性零部件阵列离线包定义持久化 开始------
  // 线性阵列定义会改变 CAD 主状态，必须在 assembly 文档解析阶段完成严格校验。
  const patternExtension = readPuppyLinearComponentPatternExtensionFromAssemblyExtensions(extensionsWithDatums)
  const parsedExtensions = writePuppyLinearComponentPatternExtensionToAssemblyExtensions(
    extensionsWithDatums,
    patternExtension,
  )
  //------ 线性零部件阵列离线包定义持久化 结束------
  return parsedExtensions
}

/**
 * 读取字符串数组。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 字符串数组。
 */
function readStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key]
  if (
    !Array.isArray(value)
    || value.some((item) => typeof item !== 'string' || item.trim().length === 0)
  ) {
    throw new Error(`PuppyCAD assembly.json 字段必须是字符串数组: ${key}`)
  }
  return value.map((item) => item.trim())
}

/**
 * 读取必填 4x4 矩阵元组。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 可直接用于 imported-model manifest 的矩阵元组。
 */
function readRequiredMatrix4Tuple(record: Record<string, unknown>, key: string): Matrix4Tuple {
  const value = record[key]
  if (
    !Array.isArray(value)
    || value.length !== 16
    || value.some((item) => typeof item !== 'number' || !Number.isFinite(item))
  ) {
    throw new Error(`PuppyCAD assembly.json 字段必须是 16 个有限数字组成的矩阵: ${key}`)
  }
  return [...value] as Matrix4Tuple
}

/**
 * 读取可选语义类型。
 *
 * @param record 记录对象。
 * @returns 语义类型。
 */
function readSemanticKind(record: Record<string, unknown>): PuppyAssemblyNode['semanticKind'] {
  const value = record.semanticKind
  return value === 'assembly' || value === 'part' || value === 'primitive' || value === 'unknown'
    ? value
    : undefined
}

/**
 * 解析装配节点。
 *
 * @param value 未知值。
 * @returns 装配节点。
 */
function parseAssemblyNode(value: unknown): PuppyAssemblyNode {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD assembly.json node 必须是对象')
  }

  const parentNodeId = value.parentNodeId
  if (parentNodeId !== null && parentNodeId !== undefined && (typeof parentNodeId !== 'string' || parentNodeId.trim().length === 0)) {
    throw new Error('PuppyCAD assembly.json parentNodeId 必须是字符串或 null')
  }
  const transform = value.transform === undefined
    ? undefined
    : readRequiredMatrix4Tuple(value, 'transform')

  return {
    nodeId: readRequiredString(value, 'nodeId'),
    parentNodeId: typeof parentNodeId === 'string' ? parentNodeId.trim() : null,
    name: readRequiredString(value, 'name'),
    nodeType: readRequiredString(value, 'nodeType'),
    path: readRequiredString(value, 'path'),
    selectable: value.selectable === true,
    semanticKind: readSemanticKind(value),
    sourceObjectId: readOptionalString(value, 'sourceObjectId'),
    transform,
    meta: readOptionalRecord(value.meta) as unknown as PuppyAssemblyNode['meta'],
    displayNodeIds: readStringArray(value, 'displayNodeIds'),
    bodyBindingIds: readStringArray(value, 'bodyBindingIds'),
    geometryObjectIds: readStringArray(value, 'geometryObjectIds'),
    extensions: readOptionalRecord(value.extensions),
  }
}

/**
 * 解析父子节点索引。
 *
 * @param value 未知值。
 * @returns 父节点到子节点 ID 列表的索引。
 */
function parseChildIdsByNodeId(value: unknown): Record<string, string[]> {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD assembly.json childIdsByNodeId 必须是对象')
  }

  return Object.fromEntries(
    Object.entries(value).map(([nodeId, childIds]) => {
      if (!Array.isArray(childIds)) {
        throw new Error(`PuppyCAD assembly.json childIdsByNodeId.${nodeId} 必须是数组`)
      }
      return [
        nodeId,
        childIds
          .map((childId) => {
            if (typeof childId !== 'string' || childId.trim().length === 0) {
              throw new Error(`PuppyCAD assembly.json childIdsByNodeId.${nodeId} 包含无效子节点 ID`)
            }
            return childId.trim()
          }),
      ]
    }),
  )
}

/**
 * 建立节点索引并拒绝重复节点。
 *
 * @param nodes 装配节点列表。
 * @returns 节点 ID 到节点的索引。
 */
function createAssemblyNodeById(nodes: readonly PuppyAssemblyNode[]): Map<string, PuppyAssemblyNode> {
  const nodeById = new Map<string, PuppyAssemblyNode>()
  nodes.forEach((node) => {
    if (nodeById.has(node.nodeId)) {
      throw new Error(`PuppyCAD assembly.json 存在重复节点 ID: ${node.nodeId}`)
    }
    nodeById.set(node.nodeId, node)
  })
  return nodeById
}

/**
 * 校验节点父子关系是否双向一致。
 *
 * @param document 待校验文档。
 * @param nodeById 节点索引。
 */
function assertAssemblyParentChildConsistency(
  document: PuppyAssemblyDocument,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
): void {
  document.nodes.forEach((node) => {
    if (node.parentNodeId && !nodeById.has(node.parentNodeId)) {
      throw new Error(`PuppyCAD assembly.json 父节点不存在: ${node.parentNodeId}`)
    }
    if (!Object.prototype.hasOwnProperty.call(document.childIdsByNodeId, node.nodeId)) {
      throw new Error(`PuppyCAD assembly.json childIdsByNodeId 缺少节点条目: ${node.nodeId}`)
    }
  })

  Object.entries(document.childIdsByNodeId).forEach(([nodeId, childIds]) => {
    if (!nodeById.has(nodeId)) {
      throw new Error(`PuppyCAD assembly.json childIdsByNodeId 包含未知父节点: ${nodeId}`)
    }
    const uniqueChildIds = new Set(childIds)
    if (uniqueChildIds.size !== childIds.length) {
      throw new Error(`PuppyCAD assembly.json childIdsByNodeId.${nodeId} 包含重复子节点`)
    }
    childIds.forEach((childId) => {
      const childNode = nodeById.get(childId)
      if (!childNode) {
        throw new Error(`PuppyCAD assembly.json childIdsByNodeId 包含未知子节点: ${childId}`)
      }
      if (childNode.parentNodeId !== nodeId) {
        throw new Error(`PuppyCAD assembly.json 父子关系不一致: ${nodeId} -> ${childId}`)
      }
    })
  })

  document.nodes.forEach((node) => {
    if (!node.parentNodeId) {
      return
    }
    const siblings = document.childIdsByNodeId[node.parentNodeId] ?? []
    if (!siblings.includes(node.nodeId)) {
      throw new Error(`PuppyCAD assembly.json 节点 parentNodeId 未被父节点 childIds 反向声明: ${node.nodeId}`)
    }
  })
}

/**
 * 校验装配树不存在循环并且所有节点都从根节点可达。
 *
 * @param document 待校验文档。
 */
function assertAssemblyTreeReachable(document: PuppyAssemblyDocument): void {
  const visiting = new Set<string>()
  const visited = new Set<string>()
  const visit = (nodeId: string): void => {
    if (visiting.has(nodeId)) {
      throw new Error(`PuppyCAD assembly.json 存在循环父子关系: ${nodeId}`)
    }
    if (visited.has(nodeId)) {
      return
    }
    visiting.add(nodeId)
    ;(document.childIdsByNodeId[nodeId] ?? []).forEach(visit)
    visiting.delete(nodeId)
    visited.add(nodeId)
  }

  visit(document.rootNodeId)
  if (visited.size !== document.nodes.length) {
    const unreachableNode = document.nodes.find((node) => !visited.has(node.nodeId))
    throw new Error(`PuppyCAD assembly.json 存在无法从根节点到达的节点: ${unreachableNode?.nodeId ?? 'unknown'}`)
  }
}

/**
 * 校验装配树父子关系。
 *
 * @param document 待校验文档。
 */
function assertAssemblyTreeIntegrity(document: PuppyAssemblyDocument): void {
  const nodeById = createAssemblyNodeById(document.nodes)
  const rootNode = nodeById.get(document.rootNodeId)
  if (!rootNode) {
    throw new Error(`PuppyCAD assembly.json 根节点不存在: ${document.rootNodeId}`)
  }
  if (rootNode.parentNodeId !== null) {
    throw new Error(`PuppyCAD assembly.json 根节点 parentNodeId 必须为 null: ${document.rootNodeId}`)
  }

  assertAssemblyParentChildConsistency(document, nodeById)
  assertAssemblyTreeReachable(document)
}

/**
 * 解析 `.puppy` 内部装配结构文档。
 *
 * @param value 未知 JSON。
 * @returns 装配结构文档。
 * @throws 当输入不是对象、版本不受支持或缺少非空 `nodes` 数组时。
 */
export function parsePuppyAssemblyDocument(value: unknown): PuppyAssemblyDocument {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD assembly.json 必须是对象')
  }
  if (value.version !== PUPPY_PACKAGE_VERSION) {
    throw new Error(`不支持的 PuppyCAD assembly.json 版本: ${String(value.version)}`)
  }
  if (!Array.isArray(value.nodes) || value.nodes.length === 0) {
    throw new Error('PuppyCAD assembly.json 缺少 nodes')
  }

  const document: PuppyAssemblyDocument = {
    version: PUPPY_PACKAGE_VERSION,
    rootNodeId: readRequiredString(value, 'rootNodeId'),
    nodes: value.nodes.map(parseAssemblyNode),
    childIdsByNodeId: parseChildIdsByNodeId(value.childIdsByNodeId),
    mates: fromAssemblyMateDocumentJSON(value.mates),
    extensions: parseAssemblyExtensions(value.extensions),
  }
  assertAssemblyTreeIntegrity(document)
  return document
}
